// webserver/src/components/AreaOverview.js
import { currentLgFieldSessionId } from '../Controls/nativeTvInputs';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './AreaOverview.css';
import axios from 'axios';
import LogicalTopologySetup from './LogicalTopologySetup';
import TopologyOverview, { evaluatePlayerSourceActivation, hdmiPort } from './TopologyOverview';
import GuidedSourceSetup from './GuidedSourceSetup';
import SetupPanel from './SetupPanel';
import TvInputManagerPanel from './TvInputManagerPanel';
import { useSocket } from '../../context/SocketContext';
import useAreaConnections from './useAreaConnections';
import useAreaObservations from './useAreaObservations';
import useAreaPowerObservations from './useAreaPowerObservations';
import { AreaPowerContext } from './DevicePowerDetails';
import usePreviewReadiness from './usePreviewReadiness';
import { inputEvidenceFresh } from '../../utils/screenActivity';

function AreaOverview({ area, siteId = area?.site_id, currentDeviceId, onDeviceSelect, onOpenDeviceRemote, onOpenAndroidPowerSettings, onOpenDevicePairing, onTopologyChange, mode = 'connections' }) {
  const socket = useSocket();
  const [devices, setDevices] = useState([]);
  const powerObservations = useAreaPowerObservations(area?.id, devices, mode === 'connections');
  const [topology, setTopology] = useState({ screens: [], audioEndpoints: [] });
  const areaKey = `${siteId}:${area?.id}`;
  const [initialLoaded, setInitialLoaded] = useState({});
  const markLoaded = useCallback((field) => setInitialLoaded((previous) => ({
    ...(previous.key === areaKey ? previous : { key: areaKey }), [field]: true,
  })), [areaKey]);
  const { liveStates, tvInputReports, requestTvState, isCurrentScope, scopeId } = useAreaObservations(
    socket, area?.id, devices, topology.screens || [], mode === 'connections'
  );
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const { remotePairing, adb: adbConnections, nativeNodes, lgTargets } = useAreaConnections(siteId, devices);
  const bootstrapReady = initialLoaded.key === areaKey && initialLoaded.devices && initialLoaded.topology;
  const indicatorsReady = devices.every((device) => liveStates[device.id]
    && adbConnections[device.id] && adbConnections[device.id].status !== 'checking'
    && (powerObservations.byId[device.id] || powerObservations.error))
    && (topology.screens || []).every((screen) => screen.displayKind === 'unmanaged'
      || inputEvidenceFresh(tvInputReports[Number(screen.televisionDeviceId)])
      || liveStates[screen.televisionDeviceId]?.available === false);
  const previewsReady = usePreviewReadiness(`${areaKey}:${scopeId}`, bootstrapReady, indicatorsReady);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [inspectedDeviceId, setInspectedDeviceId] = useState(currentDeviceId || null);
  const [tvInputRequest, setTvInputRequest] = useState({ status: 'idle', message: '', requestId: null });
  const [discoveryStatus, setDiscoveryStatus] = useState({ status: 'idle', message: '', pending: {} });
  const [guidedSourceSetupOpen, setGuidedSourceSetupOpen] = useState(false);
  const [sourceActivation, setSourceActivation] = useState({ status: 'idle', message: '', requestId: null });
  useEffect(() => {
    setTvInputRequest({ status: 'idle', message: '', requestId: null });
    setDiscoveryStatus({ status: 'idle', message: '', pending: {} });
    setSourceActivation({ status: 'idle', message: '', requestId: null });
  }, [scopeId]);
  const handleTopologyChange = useCallback((nextTopology) => {
    setTopology(nextTopology);
    onTopologyChange?.(nextTopology);
  }, [onTopologyChange]);

  useEffect(() => {
    if (currentDeviceId) setInspectedDeviceId(currentDeviceId);
  }, [currentDeviceId]);

  const handleDeviceSelect = useCallback((deviceId) => {
    setInspectedDeviceId(deviceId);
    onDeviceSelect?.(deviceId);
  }, [onDeviceSelect]);

  const retrieveTvInputs = useCallback(() => {
    if (!socket || !inspectedDeviceId) return;
    const requestId = requestTvState(inspectedDeviceId, `tv-input-manager-${inspectedDeviceId}-${Date.now()}`);
    if (!requestId) return;
    setTvInputRequest({ status: 'loading', message: 'Requesting TvInputManager data from the selected device…', requestId });
  }, [socket, inspectedDeviceId, requestTvState]);

  const discoverConnections = useCallback(() => {
    if (!socket) return;
    const televisionIds = [...new Set((topology.screens || [])
      .filter((screen) => screen.displayKind !== 'unmanaged' && screen.televisionDeviceId)
      .map((screen) => Number(screen.televisionDeviceId)))];
    if (!televisionIds.length) {
      setDiscoveryStatus({ status: 'error', message: 'No managed televisions are configured.', pending: {} });
      return;
    }
    const pending = {};
    televisionIds.forEach((deviceId) => {
      const requestId = `topology-discovery-${deviceId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const actualId = requestTvState(deviceId, requestId);
      if (actualId) pending[actualId] = deviceId;
    });
    setDiscoveryStatus({
      status: Object.keys(pending).length ? 'loading' : 'error',
      message: !Object.keys(pending).length ? 'No managed televisions are currently available for input reads.' : `Retrieving input evidence from ${televisionIds.length} managed television${televisionIds.length === 1 ? '' : 's'}…`,
      pending,
    });
  }, [socket, topology.screens, requestTvState]);

  const confirmHdmiMapping = useCallback(async (suggestion) => {
    if (!area?.id || !suggestion) return;
    const nextTopology = {
      ...topology,
      screens: (topology.screens || []).map((screen) => screen.id !== suggestion.screenId ? screen : ({
        ...screen,
        players: (screen.players || []).map((player) => Number(player.deviceId || player.id) !== Number(suggestion.playerDeviceId) ? player : ({
          ...player,
          expectedInput: suggestion.expectedInput,
          mappingMode: 'confirmed',
          mappingConfidence: suggestion.mappingConfidence,
          mappingEvidence: suggestion.mappingEvidence,
          ...(suggestion.connectionConfig ? { connectionConfig: suggestion.connectionConfig } : {}),
          lastVerifiedAt: new Date().toISOString(),
        })),
      })),
    };
    try {
      setDiscoveryStatus((current) => ({ ...current, status: 'saving', message: 'Saving confirmed HDMI mapping…' }));
      const response = await axios.put(`/api/areas/${area.id}/topology`, nextTopology, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!mounted.current || !isCurrentScope()) return;
      handleTopologyChange(response.data);
      setDiscoveryStatus({ status: 'success', message: `${suggestion.playerName} confirmed on ${suggestion.expectedInput}.`, pending: {} });
    } catch (error) {
      if (!mounted.current || !isCurrentScope()) return;
      setDiscoveryStatus({ status: 'error', message: error?.response?.data?.message || error.message || 'Failed to save HDMI mapping.', pending: {} });
    }
  }, [area?.id, handleTopologyChange, topology, isCurrentScope]);

  const requestSourceVerification = useCallback((activation, attempt) => {
    if (!socket || !activation?.televisionDeviceId) return;
    if (!mounted.current || !isCurrentScope()) return;
    const requestId = requestTvState(activation.televisionDeviceId,
      `source-verification-${activation.playerDeviceId}-${Date.now()}-${attempt}`, true);
    if (!requestId) {
      // Verification must be sampled after the switch; do not join a pre-switch read.
      window.setTimeout(() => requestSourceVerification(activation, attempt), 250);
      return;
    }
    setSourceActivation({
      ...activation,
      status: 'verifying',
      message: `Switch command sent for ${activation.playerName}; checking ${activation.expectedInput} (${attempt}/3)…`,
      requestId,
      attempt,
    });
  }, [socket, requestTvState, isCurrentScope]);

  const activatePlayerSource = useCallback(async (screen, player) => {
    const playerDeviceId = Number(player?.deviceId || player?.id);
    const televisionDeviceId = Number(screen?.televisionDeviceId);
    const expectedPort = hdmiPort(player?.expectedInput);
    const playerName = devices.find((device) => Number(device.id) === playerDeviceId)?.name || `Device ${playerDeviceId}`;
    if (!playerDeviceId || !televisionDeviceId || (!expectedPort && !player?.connectionConfig?.inputRoute?.nativeInputId)) {
      setSourceActivation({ status: 'error', message: 'Activation requires a managed TV, player, and mapped HDMI port.', requestId: null });
      return;
    }
    const requestId = `source-activation-${playerDeviceId}-${Date.now()}`;
    const activation = {
      status: 'sending',
      message: `Switching ${screen.name || 'the TV'} to ${playerName} on ${player.expectedInput || 'the saved input'}…`,
      requestId,
      playerDeviceId,
      televisionDeviceId,
      playerName,
      expectedInput: player.expectedInput,
      expectedPort,
    };
    setSourceActivation(activation);
    try {
      const response = await axios.post(
        `/api/areas/${area.id}/topology/screens/${screen.id}/activate-source`,
        { sourceDeviceId: playerDeviceId, inputOnly: true, ...(currentLgFieldSessionId(screen.siteId) ? { fieldSessionId: currentLgFieldSessionId(screen.siteId) } : {}) },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (!mounted.current || !isCurrentScope()) return;
      if (['failed', 'checkpoint_failed', 'verification_failed', 'blocked', 'manual_required', 'unsupported'].includes(response.data?.status)) {
        throw new Error(response.data?.result?.message || 'The switching routine did not complete. Check the Screen input setup.');
      }
      if (response.data?.plan?.method === 'lg_webos') {
        setSourceActivation({ ...activation, status: response.data.status === 'verified' ? 'success' : 'sent', requestId: null,
          message: response.data.status === 'verified' ? `${player.expectedInput} selected and observed on ${screen.name}. Programme content is unverified.`
            : 'TV selection sent. Input state remains unverified; observe the television.' });
        return;
      }
      setSourceActivation({ ...activation, status: 'sent', message: `${playerName} switch routine sent; waiting for live TV input status…`, requestId: null });
      window.setTimeout(() => requestSourceVerification(activation, 1), 1200);
      return;
    } catch (error) {
      if (!mounted.current || !isCurrentScope()) return;
      setSourceActivation({ status: 'error', message: error?.response?.data?.message || error.message || `Could not switch to ${playerName}.`, requestId: null });
    }
  }, [area.id, devices, requestSourceVerification, socket, isCurrentScope]);

  const activateTelevisionHome = useCallback(async (screen) => {
    const televisionDeviceId = Number(screen?.televisionDeviceId);
    if (!televisionDeviceId) {
      setSourceActivation({ status: 'error', message: 'TV Home requires a managed television.', requestId: null });
      return;
    }
    setSourceActivation({ status: 'sending', message: `Opening TV Home on ${screen.name || 'the TV'}…`, requestId: null });
    try {
      const response = await axios.post(
        screen.nativeConnector === 'lg_webos_remote'
          ? `/api/areas/${area.id}/topology/screens/${screen.id}/activate-source` : `/api/site-control/targets/${televisionDeviceId}/action`,
        screen.nativeConnector === 'lg_webos_remote' ? { destination: 'home', inputOnly: true, fieldSessionId: currentLgFieldSessionId(screen.siteId) } : { action: 'input_tv_home', repeat: 1, intervalMs: 35 },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (!mounted.current || !isCurrentScope()) return;
      setSourceActivation({
        status: response.data?.status === 'verified' ? 'success' : 'sent',
        message: response.data?.status === 'verified'
          ? `${screen.name || 'TV'} Home opened and verified.`
          : `${screen.name || 'TV'} Home sent; visible outcome is unverified.`,
        requestId: null,
      });
    } catch (error) {
      if (!mounted.current || !isCurrentScope()) return;
      setSourceActivation({ status: 'error', message: error?.response?.data?.message || error.message || 'Could not open TV Home.', requestId: null });
    }
  }, [isCurrentScope]);

  useEffect(() => {
    if (mode === 'virtual-matrix' && socket && inspectedDeviceId) retrieveTvInputs();
  }, [mode, socket, inspectedDeviceId, retrieveTvInputs]);

  useEffect(() => {
    if (tvInputRequest.status !== 'loading' || !tvInputRequest.requestId) return undefined;
    const timeout = window.setTimeout(() => {
      setTvInputRequest((current) => current.requestId === tvInputRequest.requestId
        ? { status: 'error', message: 'TvInputManager retrieval timed out.', requestId: null }
        : current);
    }, 6000);
    return () => window.clearTimeout(timeout);
  }, [tvInputRequest.status, tvInputRequest.requestId]);

  useEffect(() => {
    if (discoveryStatus.status !== 'loading' || !Object.keys(discoveryStatus.pending).length) return undefined;
    const timeout = window.setTimeout(() => {
      setDiscoveryStatus((current) => current.status === 'loading'
        ? { status: 'error', message: 'Connection discovery timed out. Existing mappings were not changed.', pending: {} }
        : current);
    }, 10000);
    return () => window.clearTimeout(timeout);
  }, [discoveryStatus.pending, discoveryStatus.status]);

  useEffect(() => {
    if (!socket) return undefined;
    const handleResponse = (payload = {}) => {
      if (!devices.some((device) => Number(device.id) === Number(payload.deviceId))) return;
      // A read can serve diagnostics and discovery together; settle both consumers.
      if (payload.type === 'state_response') {
        if (discoveryStatus.pending[payload.requestId] === Number(payload.deviceId)) {
          setDiscoveryStatus((current) => {
            const pending = { ...current.pending };
            delete pending[payload.requestId];
            const remaining = Object.keys(pending).length;
            return { status: remaining ? 'loading' : 'success', pending,
              message: remaining ? `Waiting for ${remaining} television${remaining === 1 ? '' : 's'}…`
                : 'Discovery complete. Review and confirm any suggested mappings below.' };
          });
        }
        if (payload.requestId === tvInputRequest.requestId && Number(payload.deviceId) === Number(inspectedDeviceId)) {
          const report = payload.state?.tvInputManager;
          setTvInputRequest({ status: report ? 'success' : 'error', requestId: null,
            message: report ? `TvInputManager returned ${report.inputCount ?? report.inputs?.length ?? 0} input(s).`
              : `TvInputManager retrieval failed: ${payload.error || 'no report returned'}` });
        }
      }
      if (payload.type === 'launch_suitestream_response' && Number(payload.deviceId) === Number(sourceActivation.deviceId)) {
        setSourceActivation({ status: payload.success ? 'success' : 'error', message: payload.success
          ? `Suitestream launched on ${sourceActivation.deviceName}.`
          : `Could not launch Suitestream on ${sourceActivation.deviceName}: ${payload.error || 'command failed'}.`, requestId: null });
        return;
      }
      if (payload.type === 'remote_action_response' && payload.requestId === sourceActivation.requestId && sourceActivation.status === 'sending') {
        if (!payload.success) {
          setSourceActivation((current) => ({ ...current, status: 'error', message: `Could not switch to ${current.playerName}: ${payload.error || 'action failed'}.`, requestId: null }));
          return;
        }
        window.setTimeout(() => requestSourceVerification(sourceActivation, 1), 1200);
        return;
      }
      if (payload.type === 'state_response' && payload.requestId === sourceActivation.requestId && sourceActivation.status === 'verifying') {
        const report = payload.state?.tvInputManager || null;
        const verification = evaluatePlayerSourceActivation(sourceActivation.expectedInput, report);
        const reportedPort = verification.reportedPort;
        if (verification.confirmed) {
          setSourceActivation((current) => ({
            ...current,
            status: 'confirmed',
            message: `${current.playerName} is visible on ${current.expectedInput}; confirmed by ${verification.evidenceSource || 'TV telemetry'}.`,
            requestId: null,
          }));
          return;
        }
        if (sourceActivation.attempt < 3) {
          window.setTimeout(() => requestSourceVerification(sourceActivation, sourceActivation.attempt + 1), 1000);
          return;
        }
        setSourceActivation((current) => ({
          ...current,
          status: reportedPort ? 'mismatch' : 'unverified',
          message: reportedPort
            ? `The switch ran for ${current.playerName}, but the TV still reports HDMI ${reportedPort} instead of ${current.expectedInput}.`
            : `The switch ran for ${current.playerName}, but this TV could not verify the active HDMI port.`,
          requestId: null,
        }));
        return;
      }
      if (payload.type === 'tv_input_manager_update') {
        const deviceId = Number(payload.deviceId || 0);
        const report = payload.tvInputManager || null;
        if (!report) return;
        if (deviceId !== Number(inspectedDeviceId)) return;
        const changedCount = Array.isArray(payload.changedInputIds) ? payload.changedInputIds.length : 0;
        setTvInputRequest({
          status: 'success',
          message: changedCount > 0
            ? `Live TvInputManager update received for ${changedCount} input(s).`
            : 'Live TvInputManager report received from the Android TV.',
          requestId: null,
        });
        return;
      }
    };
    const handleUnavailable = ({ deviceId, requestId } = {}) => {
      if (requestId && requestId === sourceActivation.requestId && Number(deviceId) === Number(sourceActivation.deviceId)) {
        setSourceActivation({ status: 'error', message: `${sourceActivation.deviceName} is not connected to its native Android command channel.`, requestId: null });
        return;
      }
      if (requestId && requestId === sourceActivation.requestId) {
        setSourceActivation((current) => ({
          ...current,
          status: 'error',
          message: Number(deviceId) === Number(current.playerDeviceId)
            ? `${current.playerName} is not connected to its native Android command channel.`
            : 'The television is not connected, so source activation could not be verified.',
          requestId: null,
        }));
        return;
      }
      if (requestId && discoveryStatus.pending[requestId]) {
        setDiscoveryStatus((current) => {
          const pending = { ...current.pending };
          delete pending[requestId];
          return {
            status: 'error',
            message: `A managed television is not connected to the native Android command channel. Existing mappings were not changed.`,
            pending,
          };
        });
        return;
      }
      if (Number(deviceId) !== Number(inspectedDeviceId) || tvInputRequest.status !== 'loading') return;
      if (requestId && requestId !== tvInputRequest.requestId) return;
      setTvInputRequest({ status: 'error', message: 'The selected device is not connected to the native Android command channel.', requestId: null });
    };
    socket.on('deviceCommandResponse', handleResponse);
    socket.on('deviceNotConnected', handleUnavailable);
    return () => {
      socket.off('deviceCommandResponse', handleResponse);
      socket.off('deviceNotConnected', handleUnavailable);
    };
  }, [socket, inspectedDeviceId, tvInputRequest.requestId, tvInputRequest.status, discoveryStatus.pending, requestSourceVerification, sourceActivation, devices]);

  useEffect(() => {
    if (!['sending', 'verifying'].includes(sourceActivation.status) || !sourceActivation.requestId) return undefined;
    const timeout = window.setTimeout(() => {
      setSourceActivation((current) => current.requestId === sourceActivation.requestId
        ? { ...current, status: 'error', message: 'TV source switching timed out.', requestId: null }
        : current);
    }, 8000);
    return () => window.clearTimeout(timeout);
  }, [sourceActivation.requestId, sourceActivation.status]);

  // Refresh stored device status periodically. Socket state below supplies faster live updates.
  useEffect(() => {
    let ignore = false;
    const fetchDevices = async () => {
      if (!area) return;
      const token = localStorage.getItem('token');
      try {
        const response = await fetch(`/api/areas/${area.id}/devices`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (ignore) return;
        if (response.ok) {
          const data = await response.json();
          if (ignore) return;
          setDevices(data.devices || []);
          setRefreshedAt(new Date());
        } else {
          console.error('Failed to load devices.');
        }
      } catch (error) {
        console.error('Error fetching devices:', error);
      } finally {
        if (!ignore) markLoaded('devices');
      }
    };
    fetchDevices();
    window.addEventListener('lg-player-link-changed', fetchDevices);
    const refreshTimer = window.setInterval(fetchDevices, 15000);
    return () => { ignore = true; window.clearInterval(refreshTimer); window.removeEventListener('lg-player-link-changed', fetchDevices); };
  }, [area, markLoaded]);

  useEffect(() => {
    let ignore = false;
    const fetchTopology = async () => {
      if (!area) return;
      const token = localStorage.getItem('token');
      try {
        const response = await fetch(`/api/areas/${area.id}/topology`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to load Screen connections');
        if (!ignore) handleTopologyChange(data);
      } catch (error) {
        if (!ignore) console.error('Error fetching Screen topology:', error);
      } finally {
        if (!ignore) markLoaded('topology');
      }
    };
    fetchTopology();
    window.addEventListener('lg-player-link-changed', fetchTopology);
    return () => { ignore = true; window.removeEventListener('lg-player-link-changed', fetchTopology); };
  }, [area, handleTopologyChange, markLoaded]);
  const launchSuitestream = useCallback((deviceId) => {
    if (!socket || !deviceId) return;
    const requestId = `launch-suitestream-${deviceId}-${Date.now()}`;
    socket.emit('sendCommand', { deviceId, command: 'launch_suitestream', value: null, id: requestId });
    const deviceName = devices.find((device) => Number(device.id) === Number(deviceId))?.name || `Device ${deviceId}`;
    setSourceActivation({ status: 'sent', message: `Launching Suitestream on ${deviceName}…`, requestId, deviceId, deviceName });
  }, [socket, devices]);

  return (
    <div className="area-overview">
      {mode === 'connections' && <div className="area-overview__hero">
        <div><span>Area Overview</span><h2>{area?.name || 'Area'}</h2></div>
      </div>}

      <AreaPowerContext.Provider value={powerObservations}><TopologyOverview
        siteId={siteId}
        topology={topology}
        devices={devices}
        liveStates={liveStates}
        remotePairing={remotePairing}
        adbConnections={adbConnections}
        nativeNodes={nativeNodes}
        lgTargets={lgTargets}
        tvInputReports={tvInputReports}
        discoveryStatus={discoveryStatus}
        sourceActivation={sourceActivation}
        refreshedAt={refreshedAt}
        onDiscoverConnections={discoverConnections}
        onOpenGuidedSourceSetup={() => setGuidedSourceSetupOpen(true)}
        onConfirmMapping={confirmHdmiMapping}
        onActivatePlayer={activatePlayerSource}
        onActivateTelevision={activateTelevisionHome}
        onLaunchSuitestream={launchSuitestream}
        onOpenDeviceRemote={onOpenDeviceRemote}
        onOpenAndroidPowerSettings={onOpenAndroidPowerSettings}
        onOpenDevicePairing={onOpenDevicePairing}
        onDeviceSelect={handleDeviceSelect}
        onOpenSetup={() => document.getElementById('virtual-matrix-setup')?.scrollIntoView({ behavior: 'smooth' })}
        showStatus={mode === 'virtual-matrix'}
        showConnections={mode === 'connections'}
        screenshotsEnabled={mode === 'connections'}
        screenshotScope={`${area?.id}:${scopeId}`}
        previewsReady={previewsReady}
      /></AreaPowerContext.Provider>

      {mode === 'virtual-matrix' && <>
        <header className="matrix-workspace-hero"><span>Virtual Matrix · {area?.name || 'Area'}</span><h2>Your Screens and connections</h2>
          <p>Open a Screen to manage its TV, sources and playback. Use Guided Source Setup above to discover and confirm connections; teach power behaviour in Power Control.</p></header>
        {guidedSourceSetupOpen && <GuidedSourceSetup
          areaId={area?.id}
          topology={topology}
          devices={devices}
          tvInputReports={tvInputReports}
          onTopologyChange={handleTopologyChange}
          onClose={() => setGuidedSourceSetupOpen(false)}
        />}
        <div id="virtual-matrix-setup" className="area-overview__setup">
          <LogicalTopologySetup areaId={area.id} devices={devices} currentTopology={topology} onTopologyChange={handleTopologyChange} tvInputReports={tvInputReports} />
        </div>
        <SetupPanel title="TV input diagnostics" description="Reported input inventory for the inspected TV — advanced troubleshooting">
        <TvInputManagerPanel
          device={devices.find((candidate) => Number(candidate.id) === Number(inspectedDeviceId)) || null}
          snapshot={tvInputReports[Number(inspectedDeviceId)] || null}
          status={tvInputRequest.status}
          message={tvInputRequest.message}
          onRefresh={retrieveTvInputs}
        />
        </SetupPanel>
      </>}
    </div>
  );
}

// Area changes discard local routes and pending work immediately, including when IDs overlap.
export default function ScopedAreaOverview(props) {
  return <AreaOverview key={`${props.siteId || props.area?.site_id}:${props.area?.id}:${props.mode || 'connections'}`} {...props} />;
}
